import React, { useState, useEffect, useCallback, useRef } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import ContentLayoutHeader from "../../components/layouts/ContentLayoutHeader.layout"
import Card from "../../components/atoms/Card.atom"
import Divider from "../../components/atoms/Divider.atom"
import FieldContainer from "../../components/layouts/containers/FieldContainer.container"
import InputTextForm from "../../components/molecules/InputTextForm.molecule"
import InputNumberForm from "../../components/molecules/InputNumberForm.molecule"
import DropdownInput from "../../components/molecules/DropdownInput.molecule"
import InputMaskForm from "../../components/molecules/InputMaskForm.molecule"
import InputCalendarForm from "../../components/molecules/InputCalendarForm.molecule"
import PrimaryButton from "../../components/molecules/buttons/PrimaryButton.molecule"
import TeamMembersActionModal from "./controllers/teamMembersActionModal.controller"
import { AppRoutes } from "../../consts/appRoutes.consts"
import {
  FORM_TEAM_MEMBER_HOLIDAY_SCHEMAS_IDS_WITH_ACCORDED_DAYS,
  TEAM_MEMBER_INFO_FORM_INITIAL_DATA_STATE,
  TEAM_MEMBER_INFO_FORM_INITIAL_VALIDATION_STATE,
} from "../../consts/modules/teamMembers.consts"
import { RANGES } from "../../consts/CompoundRangeDatePicker"
import { validateFieldValue } from "../../utils/validations.utils"
import {
  updateTeamMemberInfoWithUID,
  getTeamMemberInfoWithUID,
  getTeamAreas,
  getHolidaySchemas,
} from "../../services/teamMember.service"
import useToast from "../../hooks/useToast.hook"
import _ from "lodash"
import { areObjectsEqual } from "../../utils/object.utils"
import { clsx } from "../../utils/strings.utils"
import { useNavigationBlock } from "../../contexts/NavigationBlock.context"
import ModalConfirmation from "../../components/molecules/ModalConfirmation.molecule"
import useShowConfirmationTextOnPopState from "../../hooks/useShowConfirmationTextOnPopState"
import { getModifiedDateTime } from "../../utils/dateTime.utils"

const TeamMemberEditForm = () => {
  const [originalFormData, setOriginalFormData] = useState(
    _.cloneDeep(TEAM_MEMBER_INFO_FORM_INITIAL_DATA_STATE)
  )
  const [formData, setFormData] = useState(
    _.cloneDeep(TEAM_MEMBER_INFO_FORM_INITIAL_DATA_STATE)
  )
  const [validations, setValidations] = useState(
    _.cloneDeep(TEAM_MEMBER_INFO_FORM_INITIAL_VALIDATION_STATE)
  )
  const [teamAreas, setTeamAreas] = useState([])
  const [holidaySchemas, setHolidaySchemas] = useState([])

  const [loading, setLoading] = useState(true)
  const [openModal, setOpenModal] = useState(false)

  const navigate = useNavigate()
  const { state, pathname } = useLocation()

  const { setErrorMessage } = useToast()
  const {
    block,
    unblock,
    attemptedNavigation,
    resumeBlockedNavigation,
    resetAttemptedNavigation,
  } = useNavigationBlock()

  const changedFormData = !areObjectsEqual(originalFormData, formData)
  const hasErrors = Object.values(validations).some(
    (validation) => validation.errors.length > 0
  )
  const errorsRef = useRef(hasErrors)

  useEffect(() => {
    errorsRef.current = hasErrors
  }, [hasErrors])

  const blockedFormState = useRef(false)

  const madeAnEditRef = useRef(changedFormData)

  const onBeforeUnload = useCallback((e) => {
    if (!madeAnEditRef.current) return
    e.preventDefault()
    e.returnValue = false
  }, [])

  const originalStateRef = useRef(state)

  useEffect(() => {
    originalStateRef.current = state
  }, [state])

  useEffect(() => {
    const changedFormData = !areObjectsEqual(formData, originalFormData)

    if (changedFormData) {
      if (isFirefox) {
        if (!blockedFormState.current) {
          navigate(pathname, {
            replace: true,
            state: originalStateRef.current,
          })
          history.pushState(originalStateRef.current, "", location.href)
          blockedFormState.current = true
        }
      }
      block()
    } else {
      blockedFormState.current = false
      unblock()
    }

    madeAnEditRef.current = changedFormData
  }, [formData, originalFormData])

  useEffect(() => {
    window.addEventListener("beforeunload", onBeforeUnload)
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload)
      unblock()
    }
  }, [])

  const hasBlockedHistoryStateAlready = history.state === null

  const onPopState = useShowConfirmationTextOnPopState({
    onCleanup: unblock,
    errorMessage: () =>
      errorsRef.current ?
        `¡Ups! Tus cambios dejaron errores\n\nSi sales ahora, se van a descartar. ¿Seguro que quieres continuar?`
      : `¡Espera! Tienes cambios sin guardar\n\nSi sales ahora, se pierden para siempre. ¿Seguro que quieres continuar?`,
    predicate: !areObjectsEqual(formData, originalFormData),
  })

  useEffect(() => {
    getUserInfo(state.userId)
    getTeamAreaInfo()
    getHolidaySchemaInfo()
  }, [])

  const getUserInfo = async (userId) => {
    const response = await getTeamMemberInfoWithUID(userId)
    if (!response.success) {
      throw new Error(response.message)
    }
    const userData = response.result[0]
    const newFormData = {
      userFirstName: userData.name,
      userLastName: userData.lastName,
      userLastLogin: userData.lastLogin,
      teamMemberNameTag: userData.nameTag,
      userAlias: userData.alias ? userData.alias : "",
      userBirthday:
        userData.birthday ?
          getModifiedDateTime({
            dateTime: userData.birthday,
            zone: "utc",
            startOf: "day",
          })
        : null,
      userEmail: userData.email,
      userPhone: userData.phoneNumber ? userData.phoneNumber : "",
      teamMemberJobTitle: userData.jobTitle ? userData.jobTitle : "",
      userWorkAnniversay:
        userData.workAnniversary ?
          getModifiedDateTime({
            dateTime: userData.workAnniversary,
            zone: "utc",
            startOf: "day",
          })
        : null,
      teamMemberIdArea: userData.teamAreaId,
      teamMemberIdHolidaySchema:
        userData.idHolidaySchema ? userData.idHolidaySchema : null,
      teamMemberAccordedDays: userData.accordedDays ? userData.accordedDays : 0,
    }

    setOriginalFormData(newFormData)
    setFormData({ ...newFormData })
    setLoading(false)
  }

  const getTeamAreaInfo = async () => {
    const response = await getTeamAreas()
    if (!response.success) {
      throw new Error(response.message)
    }
    setTeamAreas(response.result)
  }

  const getHolidaySchemaInfo = async () => {
    const response = await getHolidaySchemas()
    if (!response.success) {
      throw new Error(response.message)
    }
    setHolidaySchemas(response.result)
  }

  const handleChange = ({ target: { name, value } }) => {
    setFormData({ ...formData, [name]: value })
    setValidations(validateFieldValue(validations, name, value))
  }

  const isFirefox = navigator.userAgent.includes("Firefox")

  const handleSubmit = async () => {
    try {
      const response = await updateTeamMemberInfoWithUID({
        userId: state.userId,
        teamMemberAccordedDays: formData.teamMemberAccordedDays,
        teamMemberIdArea: formData.teamMemberIdArea,
        teamMemberIdHolidaySchema: formData.teamMemberIdHolidaySchema,
        teamMemberJobTitle: formData.teamMemberJobTitle,
        teamMemberNameTag: formData.teamMemberNameTag,
        userAlias: formData.userAlias,
        userBirthday: formData.userBirthday?.toISODate() || null,
        userEmail: formData.userEmail,
        userFirstName: formData.userFirstName,
        userLastName: formData.userLastName,
        userPhone: formData.userPhone,
        userWorkAnniversay: formData.userWorkAnniversay.toISODate(),
      })
      if (!response.success) {
        throw new Error(response.message)
      }
    } catch (error) {
      setErrorMessage({
        title: "Error",
        message: error.message,
      })
      setLoading(false)
    }
  }

  return (
    <>
      <div id="team-member-create-form">
        <ContentLayoutHeader
          title={"Consulta de integrante"}
          subtitle={`ID: ${state.userId} | ${state.firstName} ${state.lastName}`}
          goBack
        />
        <div
          id="team-member-create-form-content"
          className={clsx(changedFormData && "sticky-footer")}
        >
          <Card className="p-1 shadow-none mb-3 ">
            <div className="flex flex-column align-items-flex-start justify-content-between w-full">
              <h3 className="font-cairo font-semibold text-3xl">
                Datos personales
              </h3>
              <div className="flex flex-row justify-content-between w-full mb-3">
                <FieldContainer col={6}>
                  <InputTextForm
                    label="Nombre"
                    name="userFirstName"
                    onChange={handleChange}
                    value={formData.userFirstName}
                    error={validations.userFirstName.errors}
                  />
                </FieldContainer>
                <FieldContainer col={6}>
                  <InputTextForm
                    label="Apellidos"
                    name="userLastName"
                    onChange={handleChange}
                    value={formData.userLastName}
                    error={validations.userLastName.errors}
                  />
                </FieldContainer>
              </div>
              <div className="flex flex-row justify-content-between w-full mb-3">
                <FieldContainer col={6}>
                  <InputTextForm
                    label="Código"
                    name="teamMemberNameTag"
                    onChange={handleChange}
                    value={formData.teamMemberNameTag}
                    error={validations.teamMemberNameTag.errors}
                  />
                </FieldContainer>
                <FieldContainer col={6}>
                  <InputTextForm
                    label="Alias"
                    name="userAlias"
                    onChange={handleChange}
                    value={formData.userAlias}
                    error={validations.userAlias.errors}
                  />
                </FieldContainer>
              </div>
              <div className="flex flex-row justify-content-between w-full">
                <FieldContainer col={6}>
                  <InputCalendarForm
                    label="Cumpleaños"
                    name="userBirthday"
                    value={formData.userBirthday}
                    period={RANGES.DAY}
                    inputReadOnly
                    allowClear={false}
                    format="DD/MM/YYYY"
                    onChange={(date) => {
                      handleChange({
                        target: { name: "userBirthday", value: date },
                      })
                    }}
                    maxDate={getModifiedDateTime({ endOf: "day" })}
                    error={validations.userBirthday.errors}
                  />
                </FieldContainer>
              </div>
            </div>
            <Divider className="mt-0 mb-3" stroke="sm" />
            <div className="flex flex-column align-items-flex-start justify-content-between w-full">
              <h3 className="font-cairo font-semibold text-3xl">
                Datos de contacto
              </h3>
              <div className="flex flex-row justify-content-between w-full">
                <FieldContainer col={6}>
                  <InputTextForm
                    label="Correo electrónico"
                    name="userEmail"
                    onChange={handleChange}
                    value={formData.userEmail}
                    error={validations.userEmail.errors}
                    disabled={formData.userLastLogin}
                  />
                </FieldContainer>
                <FieldContainer col={6}>
                  <InputMaskForm
                    label="Teléfono"
                    name="userPhone"
                    mask="+999 999 999 9999"
                    placeholder="+052 111 222 3333"
                    onChange={handleChange}
                    value={formData.userPhone}
                    error={validations.userPhone.errors}
                  />
                </FieldContainer>
              </div>
            </div>
            <Divider className="mt-0 mb-3" stroke="sm" />
            <div className="flex flex-column align-items-flex-start justify-content-between w-full">
              <h3 className="font-cairo font-semibold text-3xl">
                Información laboral
              </h3>
              <div className="flex flex-row justify-content-between w-full mb-3">
                <FieldContainer col={6}>
                  <DropdownInput
                    className="modules-dropdown"
                    label="Área"
                    name="teamMemberIdArea"
                    options={teamAreas}
                    optionLabel="name"
                    optionValue="id"
                    value={formData.teamMemberIdArea}
                    errors={validations.teamMemberIdArea.errors}
                    onChange={handleChange}
                    itemTemplate={(option) => {
                      return (
                        <div className="flex gap-2 align-items-center module-item">
                          <span className="flex-1">{option.name}</span>
                        </div>
                      )
                    }}
                  />
                </FieldContainer>
                <FieldContainer col={6}>
                  <InputTextForm
                    label="Puesto"
                    name="teamMemberJobTitle"
                    onChange={handleChange}
                    value={formData.teamMemberJobTitle}
                    error={validations.teamMemberJobTitle.errors}
                  />
                </FieldContainer>
              </div>
              <div
                className={`flex flex-row justify-content-between w-full${FORM_TEAM_MEMBER_HOLIDAY_SCHEMAS_IDS_WITH_ACCORDED_DAYS.includes(formData.teamMemberIdHolidaySchema) ? " mb-3" : ""}`}
              >
                <FieldContainer col={6}>
                  <InputCalendarForm
                    label="Fecha de ingreso"
                    name="userWorkAnniversay"
                    value={formData.userWorkAnniversay}
                    period={RANGES.DAY}
                    inputReadOnly
                    allowClear={false}
                    format="DD/MM/YYYY"
                    onChange={(date) => {
                      handleChange({
                        target: { name: "userWorkAnniversay", value: date },
                      })
                    }}
                    maxDate={getModifiedDateTime({ endOf: "day" })}
                    error={validations.userWorkAnniversay.errors}
                  />
                </FieldContainer>
                <FieldContainer col={6}>
                  <DropdownInput
                    className="modules-dropdown"
                    label="Esquema de vacaciones"
                    name="teamMemberIdHolidaySchema"
                    options={holidaySchemas}
                    optionLabel="name"
                    optionValue="id"
                    value={formData.teamMemberIdHolidaySchema}
                    errors={validations.teamMemberIdHolidaySchema.errors}
                    onChange={handleChange}
                    itemTemplate={(option) => {
                      return (
                        <div className="flex gap-2 align-items-center module-item">
                          <span className="flex-1">{option.name}</span>
                        </div>
                      )
                    }}
                  />
                </FieldContainer>
              </div>
              {(
                FORM_TEAM_MEMBER_HOLIDAY_SCHEMAS_IDS_WITH_ACCORDED_DAYS.includes(
                  formData.teamMemberIdHolidaySchema
                )
              ) ?
                <div className="flex flex-row justify-content-between w-full">
                  <FieldContainer col={6}>
                    <InputNumberForm
                      label="Días de vacaciones al año"
                      name="teamMemberAccordedDays"
                      onChange={(days) => {
                        handleChange({
                          target: {
                            name: "teamMemberAccordedDays",
                            value: days.value,
                          },
                        })
                      }}
                      value={formData.teamMemberAccordedDays}
                      error={validations.teamMemberAccordedDays.errors}
                    />
                  </FieldContainer>
                </div>
              : null}
            </div>
          </Card>
          <div className="flex align-items-center justify-content-end flex-row gap-3">
            <PrimaryButton
              icon={state.active ? "pi pi-ban" : "pi pi-check-circle"}
              className="button__secondary"
              label={state.active ? "Deshabilitar" : "Habilitar"}
              btnType="outlined"
              onClick={() => {
                setOpenModal(true)
              }}
              disabled={loading}
            />
            <PrimaryButton
              icon="pi pi-check"
              className="button__primary"
              label="Guardar"
              onClick={() => {
                setLoading(true)
                handleSubmit().then(() => {
                  unblock()
                  navigate(AppRoutes.integrantes.index)
                })
              }}
              disabled={loading || !changedFormData}
            />
          </div>
        </div>
      </div>
      <TeamMembersActionModal
        row={{
          userId: state.userId,
          active: state.active,
          firstName: state.firstName,
          lastName: state.lastName,
        }}
        visible={openModal}
        setVisible={setOpenModal}
        onFinishedSubmit={() => {
          navigate(AppRoutes.integrantes.index)
        }}
      />
      <ModalConfirmation
        closable
        content={{
          title: "¿Te quieres ir sin guardar?",
          message:
            hasErrors ?
              "A dónde vas tan rápido, tus cambios dejaron errores en tu formulario... ¿Qué quieres hacer con ellos?"
            : "Sabemos que vives al límite, pero antes... ¿Qué hacemos con tus cambios?",
          buttonCancelLabel: "Nah, bórralos",
          buttonSaveLabel: hasErrors ? "Volver y corregir" : "Guarda, porfa",
        }}
        onAccept={() => {
          if (hasErrors) {
            resetAttemptedNavigation()
          } else {
            setLoading(true)
            handleSubmit()
              .then(() => {
                window.removeEventListener("popstate", onPopState)
                window.removeEventListener("beforeunload", onBeforeUnload)

                if (hasBlockedHistoryStateAlready) {
                  history.back()
                }
                unblock()
                setTimeout(() => {
                  resumeBlockedNavigation()
                })
              })
              .catch(() => {
                resetAttemptedNavigation()
              })
          }
        }}
        onRequestCancel={() => {
          window.removeEventListener("popstate", onPopState)
          window.removeEventListener("beforeunload", onBeforeUnload)
          if (hasBlockedHistoryStateAlready) {
            history.back()
          }
          unblock()
          setTimeout(
            () => {
              resumeBlockedNavigation()
            },
            isFirefox ? 15 : 0
          )
        }}
        loading={loading}
        disabled={loading}
        visible={attemptedNavigation[0]}
        onHide={() => {
          resetAttemptedNavigation()
        }}
      />
    </>
  )
}

export default TeamMemberEditForm
